import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '_store';
import { safeReturnPath } from './returnPath';
import { fetchAuthMethods } from './authentikFlow';
import { authentikHost, redirectToAuthentik } from './authentikRedirect';
import { AuthentikLoginForm, FormAlert } from './AuthentikLoginForm';
import { LocalLoginForm } from './LocalLoginForm';

import logo from '../res/logo.svg';
import { IconLogo } from '_components/icons';
import './login.css';

export { Login };

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { authUser, setAuthUser, setUserRole, loading } = useContext(AuthContext);
    const [methods, setMethods] = useState(null);
    const [localOpen, setLocalOpen] = useState(searchParams.get('method') === 'local');
    const [redirecting, setRedirecting] = useState(false);
    const [ssoBusy, setSsoBusy] = useState(false);

    const returnPath = safeReturnPath(location.state?.from);
    const callbackError = searchParams.get('error');
    const sessionMessage = location.state?.reason === 'expired'
        ? 'Your session expired. Please sign in again.'
        : location.state?.reason === 'remote'
            ? 'You were signed out in another tab.'
            : null;

    // Check if user is already authenticated
    useEffect(() => {
        if (authUser && !loading) {
            navigate(returnPath, { replace: true });
        }
    }, [authUser, loading, navigate, returnPath]);

    useEffect(() => {
        let cancelled = false;
        fetchAuthMethods().then((result) => {
            if (!cancelled) setMethods(result);
        });
        return () => { cancelled = true; };
    }, []);

    const handleAuthenticated = useCallback((data) => {
        const { role, roles } = data;
        setAuthUser(data);
        setUserRole(role || roles || 'user');
        navigate(returnPath, { replace: true });
    }, [navigate, returnPath, setAuthUser, setUserRole]);

    const sso = methods?.sso;
    const handleRedirect = useCallback(() => {
        setRedirecting(true);
        redirectToAuthentik(returnPath, sso);
    }, [returnPath, sso]);

    const ssoEnabled = Boolean(sso?.enabled);
    const ssoInline = ssoEnabled && Boolean(sso?.inline);
    const idpHost = authentikHost(sso);
    // Without SSO the local form is the only way in, so show it expanded.
    const showLocal = methods && (!ssoEnabled || localOpen);

    return (
        <div className="login-page-bg font-body min-h-screen flex flex-col items-center justify-center px-4 py-10 antialiased">
            <main className="w-full max-w-[420px] flex flex-col items-center">
                <header className="mb-8 flex flex-col items-center">
                    <img src={logo} alt="NetX" className="login-brand-logo login-brand-logo-light" />
                    <IconLogo
                        height={56}
                        className="login-brand-logo login-brand-logo-dark"
                        brandColor="var(--color-text-strong)"
                        accentColor="var(--color-accent-teal)"
                        accentOpacity={0.5}
                    />
                </header>

                <section className="login-card w-full" aria-label="Sign in to NetX Stats">

                    {sessionMessage && <div className="login-session-message" role="status">{sessionMessage}</div>}
                    {callbackError && <FormAlert message={callbackError} />}

                    {!methods && (
                        <div className="login-flow-loading" role="status">
                            <span className="login-spinner" aria-hidden="true"></span>
                            Loading sign-in options…
                        </div>
                    )}

                    {redirecting && (
                        <div className="login-flow-loading" role="status">
                            <span className="login-spinner" aria-hidden="true"></span>
                            Continuing on {idpHost}…
                        </div>
                    )}

                    {!redirecting && ssoInline && (
                        <AuthentikLoginForm
                            onAuthenticated={handleAuthenticated}
                            onFallback={handleRedirect}
                            onBusyChange={setSsoBusy}
                        />
                    )}

                    {!redirecting && ssoEnabled && !ssoInline && (
                        <button type="button" className="login-btn login-btn-primary" onClick={handleRedirect}>
                            <span className="material-symbols-outlined" aria-hidden="true">shield_person</span>
                            Continue with NETX account
                        </button>
                    )}

                    {!redirecting && !ssoEnabled && showLocal && (
                        <LocalLoginForm onAuthenticated={handleAuthenticated} primary />
                    )}

                    {!redirecting && ssoInline && (
                        <p className="login-alt">
                            Using a passkey, Google or another provider?{' '}
                            <button type="button" className="login-link" onClick={handleRedirect} disabled={ssoBusy}>
                                Continue on {idpHost}
                                <span className="material-symbols-outlined" aria-hidden="true">open_in_new</span>
                            </button>
                        </p>
                    )}
                </section>

                {methods && ssoEnabled && !redirecting && (
                    <section className="login-legacy w-full">
                        <button
                            type="button"
                            className="login-disclosure"
                            aria-expanded={localOpen}
                            aria-controls="login-legacy-panel"
                            onClick={() => setLocalOpen((open) => !open)}
                        >
                            <span className="material-symbols-outlined" aria-hidden="true">
                                {localOpen ? 'expand_less' : 'expand_more'}
                            </span>
                            Local account
                        </button>
                        {localOpen && (
                            <div id="login-legacy-panel" className="login-legacy-panel">
                                <p className="login-hint">
                                    For accounts created directly in NetX Stats. New users sign in with their NETX account.
                                </p>
                                <LocalLoginForm onAuthenticated={handleAuthenticated} />
                            </div>
                        )}
                    </section>
                )}

                <footer className="login-footer">© {new Date().getFullYear()} NETX Networks.</footer>
            </main>
        </div>
    );
}
